import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { Loading, PageHeader } from '~/components';
import { RelativeTime } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { CardAccount, CardAccountRepository } from '~/repositories/CardAccountRepository';
import { CardProgram, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

const formatStatus = (status: string): string => {
  if (!status) return '-';
  return formatString(status);
};

const columns = (cardProgramsMap: Map<string, CardProgram>): TableColumn<CardAccount>[] => [
  {
    header: 'Card Account ID',
    accessor: 'id',
    width: { min: 200, max: '3fr' },
    cell: ({ value }) => <Table.TextCell displayVariant="monospaced" value={value ?? ''} />,
  },
  {
    header: 'Card Program',
    accessor: 'cardProgramId',
    width: { min: 140, max: '2fr' },
    cell: ({ value }) => {
      const program = cardProgramsMap.get(value ?? '');
      return <Table.TextCell value={program?.description || value || '-'} />;
    },
  },
  {
    header: 'Account Type',
    id: 'accountType',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ row }) => {
      const program = cardProgramsMap.get(row.cardProgramId ?? '');
      return <Table.ChipCell value={program?.cardProgramType ? formatString(program.cardProgramType) : '-'} />;
    },
  },
  {
    header: 'Status',
    accessor: 'status',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={formatStatus(value ?? '')} />,
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => (value ? <RelativeTime timestamp={value as Date} /> : <Table.TextCell value="-" />),
  },
];

export const PageCardAccountsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();

  const { data: cardAccountsData, isLoading: isCardAccountsLoading } = useQuery({
    queryKey: ['card-accounts', 'list', currentPlatform?.id, isSandbox],
    queryFn: () => CardAccountRepository.listCardAccounts(),
    enabled: !!currentPlatform?.id,
  });

  const { data: cardProgramsData } = useQuery({
    queryKey: ['card-programs', 'list', currentPlatform?.id, isSandbox],
    queryFn: () => CardProgramRepository.listCardPrograms(),
    enabled: !!currentPlatform?.id,
  });

  const cardProgramsMap = new Map<string, CardProgram>();
  (cardProgramsData?.cardPrograms ?? []).forEach((program) => {
    cardProgramsMap.set(program.id, program);
  });

  return (
    <>
      <PageHeader text="Card Accounts" border={false} />
      <Loading isLoading={isCardAccountsLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <Table<CardAccount>
            columns={columns(cardProgramsMap)}
            data={cardAccountsData?.cardAccounts ?? []}
            isLoading={isCardAccountsLoading}
            variant={TableVariant.List}
            onRowClick={(row) => {
              navigate(`${ROUTE.CARD_ACCOUNTS}/edit/${row.id}`);
            }}
          />
        </Inner>
      </Loading>
    </>
  );
};
